import React from 'react';

import { QueryOptions } from '@apollo/client';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';

import { DateFormatPatterns, DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';
import { formatDate, getDateTimeInTimezone, parseNullableValue } from '@clearverify/utils';

import {
  FileResponseInfoFragment,
  GeneratedReportMetaInfoFragment,
  InboxItemDocument,
  InboxItemQueryVariables,
  InboxItemsScrollableListDocument,
  InboxItemsScrollableListQueryVariables,
  PiiDataAccessActionType,
} from 'src/api';
import { Modal, ModalTopAction } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { GeneratedReportsPdfRenderer } from 'src/components/generatedReport';
import { GeneratedReportPreviewCardAction } from 'src/components/generatedReport/GeneratedReportPreviewCard';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import {
  useApiVerificationRequest,
  useVerifierByApiVerificationRequestId,
} from 'src/hooks/apiVerificationRequest';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemUnreadStatuses } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import type { SxProp } from 'src/types/mui';

import { ViewApiVerificationRequestReportModalArgs } from './types';

// The report-viewing experience mirrors ViewVerificationRequestReportModal: when a verifier is
// present we surface the same basic verifier header, otherwise it is just preview and download of
// the shared report(s). Api verification requests carry no permissible-purpose/payment header and
// no dispute action.
export const ViewApiVerificationRequestReportModal: React.FC = () => {
  const { spawnNotification } = useNotification();
  const { isMobile } = useScreenInfo();
  const { timezone } = useCurrentUser();

  const { closeModal, isOpen, args } = useModal<ViewApiVerificationRequestReportModalArgs>(
    ModalId.ViewApiVerificationRequestReportModal,
  );

  const { apiVerificationRequest } = useApiVerificationRequest({
    inboxItemId: args?.inboxItemId,
    skip: !isOpen,
  });

  const { isEmployee, hasVerifierRoles, hasPlatformRoles } = useCurrentUserPermissions();

  // Verifier identity is only surfaced to the employee being verified, the verifier itself, and
  // Clear Verify (platform) staff — never to employer/PEO viewers.
  const canViewVerifier = isEmployee || hasVerifierRoles || hasPlatformRoles;
  const { verifier } = useVerifierByApiVerificationRequestId({
    inboxItemId: args?.inboxItemId,
    skip: !isOpen || !canViewVerifier,
  });

  const { generatedReportsMeta, downloadGeneratedReportFiles, downloading } = useGeneratedReports({
    inboxItemId: args?.inboxItemId,
    skip: !isOpen,
  });

  const { updateInboxItemUnreadStatuses } = useInboxItemUnreadStatuses(args?.inboxItemId);

  const [preview, setPreview] = React.useState<FileResponseInfoFragment | null>(null);

  const modalTitle = args?.reportType ? VERIFICATION_REQUEST_TYPE_LABEL[args.reportType] : '';

  const handleCloseModal = React.useCallback(() => {
    closeModal();
    setPreview(null);
    updateInboxItemUnreadStatuses();
  }, [closeModal, updateInboxItemUnreadStatuses]);

  const refetchQueriesOnAction = React.useMemo(
    (): QueryOptions[] => [
      {
        query: InboxItemsScrollableListDocument,
        variables: {
          where: { id: { equals: args?.inboxItemId || '' } },
        } satisfies InboxItemsScrollableListQueryVariables,
      },
      {
        query: InboxItemDocument,
        variables: { id: args?.inboxItemId || '' } satisfies InboxItemQueryVariables,
      },
    ],
    [args?.inboxItemId],
  );

  const handlePreview = React.useCallback(
    async (report: GeneratedReportMetaInfoFragment) => {
      const [previewFile] = await downloadGeneratedReportFiles(
        report.file.id,
        PiiDataAccessActionType.Viewed,
        { refetchQueries: refetchQueriesOnAction },
      );
      if (previewFile) {
        setPreview({ ...previewFile, filename: report.employerDisplayName ?? 'Unknown employer' });
      }
    },
    [downloadGeneratedReportFiles, refetchQueriesOnAction],
  );

  const handleResetPreview = React.useCallback(() => {
    setPreview(null);
  }, []);

  const handleDownload = React.useCallback(
    async (reportFileId: string) => {
      await downloadGeneratedReportFiles(reportFileId, PiiDataAccessActionType.Downloaded, {
        refetchQueries: refetchQueriesOnAction,
      });
    },
    [downloadGeneratedReportFiles, refetchQueriesOnAction],
  );

  const handleDownloadAllReports = React.useCallback(async () => {
    if (!generatedReportsMeta.length) {
      spawnNotification("There's no reports yet", 'error');
      return;
    }

    await downloadGeneratedReportFiles(
      generatedReportsMeta.map(report => report.file.id),
      PiiDataAccessActionType.Downloaded,
      { refetchQueries: refetchQueriesOnAction },
    );
    await updateInboxItemUnreadStatuses();
  }, [
    generatedReportsMeta,
    downloadGeneratedReportFiles,
    refetchQueriesOnAction,
    updateInboxItemUnreadStatuses,
    spawnNotification,
  ]);

  const cardActions = React.useMemo<GeneratedReportPreviewCardAction[]>(
    () => [
      {
        name: 'Download',
        icon: <DownloadIcon />,
        onAction: generatedReport => {
          if (!generatedReport) return;
          downloadGeneratedReportFiles(
            generatedReport.file.id,
            PiiDataAccessActionType.Downloaded,
            {
              refetchQueries: refetchQueriesOnAction,
            },
          );
        },
      },
    ],
    [downloadGeneratedReportFiles, refetchQueriesOnAction],
  );

  const mobileTopAction = React.useMemo(
    (): ModalTopAction => ({
      show: true,
      render: () => (
        <IconButton onClick={handleCloseModal} disabled={downloading}>
          <CloseIcon />
        </IconButton>
      ),
    }),
    [handleCloseModal, downloading],
  );

  const topAction = React.useMemo(
    (): ModalTopAction => ({
      actionName: 'Download All',
      show: true,
      icon: <DownloadIcon />,
      disabled: downloading,
      onAction: () => handleDownloadAllReports(),
    }),
    [handleDownloadAllReports, downloading],
  );

  return (
    <Modal
      title={`${modalTitle} Report`}
      topAction={isMobile ? mobileTopAction : topAction}
      dialogProps={{
        fullWidth: true,
        fullScreen: isMobile,
        maxWidth: 'md',
        open: isOpen,
        onClose: handleCloseModal,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            display="grid"
            gridTemplateColumns={isMobile ? '1fr' : '220px minmax(0, 1fr)'}
            columnGap={2}
            rowGap={1}
            alignItems="start"
          >
            <Typography color="GrayText" variant="body1" sx={textStyles}>
              Report Type:
            </Typography>
            <Typography color="black" variant="body1" sx={{ overflowWrap: 'anywhere' }}>
              {apiVerificationRequest?.type
                ? VERIFICATION_REQUEST_TYPE_LABEL[apiVerificationRequest.type]
                : DEFAULT_VALUE_IF_NIL}
            </Typography>

            <Typography color="GrayText" variant="body1" sx={textStyles}>
              Date Shared:
            </Typography>
            <Typography color="black" variant="body1">
              {formatDate(
                getDateTimeInTimezone(apiVerificationRequest?.createdAt, timezone)?.toISO(),
                DateFormatPatterns.meridiemTimePaddedWithDate,
              )}
            </Typography>

            {/* Verifier identity — only the employee, the verifier, and Clear Verify staff. */}
            {verifier && (
              <React.Fragment>
                <Typography color="GrayText" variant="body1" sx={textStyles}>
                  Verifier Company Name:
                </Typography>
                <Typography color="black" variant="body1" sx={{ overflowWrap: 'anywhere' }}>
                  <DemoPrivacyText>{parseNullableValue(verifier.displayName)}</DemoPrivacyText>
                </Typography>

                <Typography color="GrayText" variant="body1" sx={textStyles}>
                  Verifier Contact Email:
                </Typography>
                <Typography color="black" variant="body1" sx={{ overflowWrap: 'anywhere' }}>
                  <DemoPrivacyText>{parseNullableValue(verifier.contactEmail)}</DemoPrivacyText>
                </Typography>

                <Typography color="GrayText" variant="body1" sx={textStyles}>
                  Verifier Contact Phone:
                </Typography>
                <Typography color="black" variant="body1" sx={{ overflowWrap: 'anywhere' }}>
                  <DemoPrivacyText>{parseNullableValue(verifier.contactPhone)}</DemoPrivacyText>
                </Typography>
              </React.Fragment>
            )}
          </Box>
        </Grid>

        <Grid size={12}>
          {generatedReportsMeta.length ? (
            <Box sx={reportBoxStyles} p={3}>
              <GeneratedReportsPdfRenderer
                pdfPreview={preview}
                onPreview={handlePreview}
                onResetPreview={handleResetPreview}
                onDownload={handleDownload}
                generatedReportsMeta={generatedReportsMeta}
                cardActions={cardActions}
              />
            </Box>
          ) : (
            <Typography variant="body1">
              The report is generating. Please try again later.
            </Typography>
          )}
        </Grid>

        <Grid size={12}>
          <Box display="flex" justifyContent="center" mt={isMobile ? 1 : 2}>
            <Button variant="outlined" color="infoButton" onClick={handleCloseModal}>
              Close
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Modal>
  );
};

const textStyles: SxProp = theme => ({
  opacity: 1,
  [theme.breakpoints.down('sm')]: {
    opacity: 0.6,
  },
});

const reportBoxStyles: SxProp = theme => ({
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: 2,
});
